import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutDashboard, Kanban, Users, Activity, FileText, Receipt, Upload, MessagesSquare,
  Calculator, BarChart3, ShieldCheck, Landmark, UserCog, LineChart, Settings,
  ChevronLeft, Home, Flame, Mail, Trophy, PhoneOutgoing, Compass, Sparkles, Store,
} from 'lucide-react';
import { navSections, brokerUser, company } from '../../mock';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/authStore';

const ICONS = {
  LayoutDashboard, Kanban, Users, Activity, FileText, Receipt, Upload, MessagesSquare,
  Calculator, BarChart3, ShieldCheck, Landmark, UserCog, LineChart, Settings, Flame, Mail, Trophy, PhoneOutgoing, Compass, Sparkles, Store,
};

// Admin-only nav section. Hidden for loan officers; appears under the
// Workspace block when the authenticated user has role === 'admin'.
const ADMIN_SECTION = {
  label: 'Admin',
  items: [
    { key: 'mailer', label: 'Mailer', icon: 'Mail', to: '/mailer' },
  ],
};

// Brand wordmark in the top-left of the sidebar. The full logo (Kinfolk
// wordmark + Home Loans label, with the house-in-O motif) is the company's
// official brand asset — hosted on the Emergent CDN so the bundle stays
// small. Compact mode (collapsed sidebar) falls back to the standalone
// house mark.
const LOGO_FULL_URL = 'https://customer-assets.emergentagent.com/job_broker-hub-redesign/artifacts/2316od8g_Kinfolk-Logo_FINAL_Black%20%281%29%20%281%29.png';

function KinfolkLogo({ compact }) {
  if (compact) {
    return (
      <div className="flex items-center gap-2.5">
        <div className="relative h-9 w-9 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center overflow-hidden shadow-sm">
          <Home className="relative h-4 w-4 text-white" strokeWidth={2.5} />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center">
      <img
        src={LOGO_FULL_URL}
        alt="Kinfolk Home Loans"
        className="h-12 w-auto object-contain select-none dark:invert"
        draggable={false}
      />
    </div>
  );
}

export default function Sidebar({ expanded = true, onToggleExpanded, mobileOpen = false, onCloseMobile }) {
  const location = useLocation();
  const { isAdmin: admin } = useAuth();
  // Splice the Admin section in after Workspace so it sits near the top.
  // For non-admins, drop the "Team" nav item (admin-only route) so it never
  // dead-clicks to the dashboard.
  const filteredBase = admin
    ? navSections
    : navSections.map((s) => ({ ...s, items: s.items.filter((it) => it.key !== 'team' && it.key !== 'reports' && it.key !== 'settings') }));
  const sections = admin
    ? [filteredBase[0], ADMIN_SECTION, ...filteredBase.slice(1)]
    : filteredBase;

  // On mobile the drawer always renders at full width (272px) regardless of
  // the desktop "expanded" preference. On desktop the chevron toggle drives
  // the collapsed (80px) vs expanded (272px) width.
  const open = mobileOpen || expanded;

  return (
    <>
      {/* Mobile backdrop — taps dismiss the drawer */}
      {mobileOpen && (
        <button
          aria-label="Close menu"
          onClick={onCloseMobile}
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm animate-fadeIn"
        />
      )}
      <aside
        className={cn(
          // Base — flex column container, fixed width on mobile, brand bg
          'bg-white dark:bg-neutral-950 border-r hairline flex flex-col w-[280px]',
          // Mobile (default): off-canvas drawer; toggles via translate-x.
          'fixed inset-y-0 left-0 z-50 transition-transform duration-300 ease-out',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop (lg+): override fixed-drawer with sticky sidebar that
          // pins to the viewport top while the main scrolls. Width animates
          // between collapsed (80px) and expanded (272px).
          'lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 lg:z-auto lg:transition-[width] lg:duration-300 lg:ease-out',
          expanded ? 'lg:w-[272px]' : 'lg:w-20',
        )}
      >
      {/* Brand */}
      <div className="px-5 h-16 flex items-center justify-between border-b hairline">
        <KinfolkLogo compact={!open} />
        {/* Desktop collapse chevron */}
        <button
          onClick={onToggleExpanded}
          className="hidden lg:flex h-7 w-7 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 items-center justify-center text-neutral-500"
          aria-label="Collapse"
        >
          <ChevronLeft className={cn('h-4 w-4 transition-transform', !expanded && 'rotate-180')} />
        </button>
        {/* Mobile close button */}
        <button
          onClick={onCloseMobile}
          className="lg:hidden h-8 w-8 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center justify-center text-neutral-500"
          aria-label="Close menu"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 pt-4 pb-4 space-y-5">
        {sections.map((section) => (
          <div key={section.label}>
            {open && (
              <div className="px-3 mb-1.5 flex items-center gap-2">
                <div className="h-px flex-1 bg-neutral-200" />
                <span className="text-[9px] uppercase tracking-[0.24em] text-neutral-400 font-semibold">
                  {section.label}
                </span>
                <div className="h-px flex-1 bg-neutral-200" />
              </div>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const Icon = ICONS[item.icon] || Activity;
                // Active match — require either an exact pathname hit
                // or a strict child route (`/leads/` not `/leads-…`).
                // Previously used a plain `startsWith(item.to)` which
                // lit up "Leads" when the user was on
                // `/leads-marketplace` because that string literally
                // starts with `/leads`.
                const active = location.pathname === item.to
                  || (item.to !== '/' && location.pathname.startsWith(item.to + '/'));
                return (
                  <NavLink
                    key={item.key}
                    to={item.to}
                    end={item.to === '/'}
                    data-testid={item.key === 'team' ? 'sidebar-team-link' : `sidebar-nav-${item.key}`}
                    className={cn(
                      'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors',
                      active
                        ? 'bg-neutral-900 text-white'
                        : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                    )}
                    title={!open ? item.label : undefined}
                  >
                    {active && <span className="absolute right-2 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-[hsl(var(--kf-gold))]" />}
                    <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-white' : 'text-neutral-400 group-hover:text-neutral-900')} strokeWidth={2} />
                    {open && <span className="truncate">{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User */}
      <div className="p-3 border-t hairline">
        <UserPanel open={open} admin={admin} />
      </div>
    </aside>
    </>
  );
}


function UserPanel({ open, admin }) {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [changePwOpen, setChangePwOpen] = React.useState(false);
  const display = user?.name || 'Signed in';
  const initials = user?.initials || (user?.name ? user.name.slice(0, 2).toUpperCase() : 'KF');
  const subtitle = user?.title || (user?.role === 'admin' ? 'Administrator' : 'Loan Officer');

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        data-testid="sidebar-user-button"
        className={cn('w-full flex items-center gap-3 rounded-xl p-2 text-left hover:bg-neutral-50 transition', open && 'bg-neutral-50')}
      >
        <div className="h-9 w-9 rounded-full bg-neutral-900 text-white flex items-center justify-center font-bold text-[12px] shrink-0 border hairline">
          {initials}
        </div>
        {open && (
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-semibold text-neutral-900 truncate flex items-center gap-1.5">
              {display}
              {admin && (
                <span className="text-[8.5px] uppercase tracking-wider font-bold bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border border-[hsl(var(--kf-gold))]/40 rounded px-1 py-px leading-none">
                  Admin
                </span>
              )}
            </div>
            <div className="text-[10.5px] text-neutral-500 truncate">{subtitle} · {company.nmls}</div>
          </div>
        )}
      </button>

      {menuOpen && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setMenuOpen(false)} />
          <div className="absolute left-2 right-2 bottom-[calc(100%+4px)] z-[70] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden" data-testid="sidebar-user-menu">
            <div className="px-3 py-2.5 border-b hairline">
              <div className="text-[12px] font-semibold text-neutral-900 truncate">{user?.name}</div>
              <div className="text-[10.5px] text-neutral-500 truncate">{user?.email}</div>
            </div>
            <button
              type="button"
              data-testid="sidebar-change-password-btn"
              onClick={() => { setMenuOpen(false); setChangePwOpen(true); }}
              className="block w-full text-left px-3 py-2 text-[12px] font-semibold text-neutral-700 hover:bg-neutral-50 transition"
            >
              Change password
            </button>
            {admin && (
              <NavLink
                to="/team"
                onClick={() => setMenuOpen(false)}
                className="block w-full text-left px-3 py-2 text-[12px] font-semibold text-neutral-700 hover:bg-neutral-50 transition border-t hairline"
                data-testid="sidebar-team-link"
              >
                Manage team
              </NavLink>
            )}
            <button
              type="button"
              data-testid="sidebar-logout-btn"
              onClick={async () => {
                const { logout } = await import('../../lib/authStore');
                await logout();
                window.location.replace('/login');
              }}
              className="block w-full text-left px-3 py-2 text-[12px] font-semibold text-red-600 hover:bg-red-50 transition border-t hairline"
            >
              Sign out
            </button>
          </div>
        </>
      )}

      {changePwOpen && <ChangePasswordModal onClose={() => setChangePwOpen(false)} />}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Change password — every user can rotate their own password without */
/* needing an admin. POST /api/auth/change-password verifies the      */
/* current password before swapping; UI also enforces 8-char minimum  */
/* and a confirm field so a typo can't lock them out.                 */
function ChangePasswordModal({ onClose }) {
  // Mounted only when open (parent uses a conditional). State resets
  // naturally on every open because we remount.
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = React.useCallback(async (e) => {
    e.preventDefault();
    if (saving) return;
    if (next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (next !== confirm) { toast.error('Passwords do not match'); return; }
    if (next === current) { toast.error('New password must be different from current'); return; }
    setSaving(true);
    try {
      const axios = (await import('axios')).default;
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`,
        { current_password: current, new_password: next },
      );
      toast.success('Password updated — use the new password next time you log in');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update password');
    } finally { setSaving(false); }
  }, [saving, next, confirm, current, onClose]);
  return (
    <div
      data-testid="change-password-modal"
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white rounded-2xl border hairline shadow-2xl p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-[17px] font-bold text-neutral-900">Change password</h2>
          <p className="mt-1 text-[12px] text-neutral-500">For security, enter your current password to make changes.</p>
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-500 mb-1.5 block">Current password</span>
          <input
            type="password"
            data-testid="change-password-current"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="w-full h-11 px-3 rounded-md border hairline bg-white text-[14px] focus:outline-none focus:border-neutral-900 transition"
            autoFocus
            autoComplete="current-password"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-500 mb-1.5 block">New password</span>
          <input
            type="password"
            data-testid="change-password-new"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="w-full h-11 px-3 rounded-md border hairline bg-white text-[14px] focus:outline-none focus:border-neutral-900 transition"
            autoComplete="new-password"
            placeholder="At least 8 characters"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-500 mb-1.5 block">Confirm new password</span>
          <input
            type="password"
            data-testid="change-password-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full h-11 px-3 rounded-md border hairline bg-white text-[14px] focus:outline-none focus:border-neutral-900 transition"
            autoComplete="new-password"
          />
        </label>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="change-password-cancel"
            className="h-10 px-4 rounded-md border hairline bg-white text-[12.5px] font-bold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !current || !next || !confirm}
            data-testid="change-password-submit"
            className="h-10 px-4 rounded-md btn-ink text-[12.5px] font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </form>
    </div>
  );
}


